import { Dispatch, SetStateAction, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Box, Button, Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import {
  AutocompleteOption,
  ControlledAutocomplete,
  ControlledCheckboxField,
  ControlledFileUpload,
  ControlledSelect,
  ControlledTextField,
} from '@/components/inputs'
import {
  useMutationWrapper,
  usePatchObservationLog,
  usePostSoundRecord,
} from '@/hooks'
import {
  ObservationLog,
  ObservationTypeCode,
  SoundRecord,
  SoundRecordStatus,
  SoundReportStatuses,
} from '@/types'
import {
  AMPHIBIANS_SPECIE_OPTIONS,
  BATS_SPECIE_OPTIONS,
  BIRDS_BREEDING_SPECIE_OPTIONS,
  UNKNOWN_BAT_SPECIE_OPTION_VALUE,
  OTHER_SPECIE_OPTION_VALUE,
} from '@/components/forms'
import { SelectOption } from '@/components'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import translation from 'i18next'

export type SoundRecordFormData = {
  spectrogram_link: File | undefined
  specie_identified: AutocompleteOption[] | string
  unknown_specie?: string
  other_specie?: string
  analysis_notes: string
  analysis_confidence: number
  analysis_status: SoundRecordStatus
  reject_reason: string
  second_opinion_reason: string
  change_observation_log_specie?: boolean
}

const schema = yup.object({
  analysis_confidence: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .typeError(translation.t('errors:validation.numberType'))
    .optional(),
  reject_reason: yup.string().when('analysis_status', {
    is: SoundReportStatuses.REJECTED,
    then: (schema) =>
      schema.required(translation.t('errors:validation.required')),
    otherwise: (schema) => schema.optional(),
  }),
  second_opinion_reason: yup.string().when('analysis_status', {
    is: SoundReportStatuses.SECOND_OPINION,
    then: (schema) =>
      schema.required(translation.t('errors:validation.required')),
    otherwise: (schema) => schema.optional(),
  }),
})

export type SoundRecordFormContainerProps = {
  researchObservationLogType: ObservationTypeCode
  setUpdatedObservationLog?: Dispatch<SetStateAction<ObservationLog>>
  data: SoundRecord
  setUpdatedSoundRecord?: (data: SoundRecord) => void
  onSuccessUpdate: () => void
}

const getSpeciesOptionsByType = (type: string): SelectOption[] | undefined => {
  switch (type) {
    case 'bats':
      return BATS_SPECIE_OPTIONS
    case 'bats_feeding_area':
      return BATS_SPECIE_OPTIONS
    case 'bats_flight_path':
      return BATS_SPECIE_OPTIONS
    case 'birds_breeding':
      return BIRDS_BREEDING_SPECIE_OPTIONS
    case 'amphibian_fish':
      return AMPHIBIANS_SPECIE_OPTIONS
    case 'insects':
      return undefined
    default:
      return undefined
  }
}

export const SoundRecordFormContainer = ({
  researchObservationLogType,
  setUpdatedObservationLog,
  data,
  setUpdatedSoundRecord,
  onSuccessUpdate,
}: SoundRecordFormContainerProps): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const { mutateAsync: editSoundRecord, isLoading: mutateInProcess } =
    useMutationWrapper(() => usePostSoundRecord(data.id))

  const {
    mutateAsync: updateObservationLog,
    isLoading: mutateObservationLogInProcess,
  } = useMutationWrapper(() =>
    usePatchObservationLog({
      id: data.observation_log.id,
    })
  )

  const speciesOptions = getSpeciesOptionsByType(researchObservationLogType)

  const acOptions = speciesOptions
    ? speciesOptions.map((option) => ({
        value: option.value,
        title: option.name,
      }))
    : undefined

  const defaultValues = useMemo(() => {
    let speciesIdentified: AutocompleteOption[] | string

    if (
      speciesOptions &&
      data.specie_identified?.includes(`${OTHER_SPECIE_OPTION_VALUE}:`)
    ) {
      const fs = speciesOptions.find(
        (s) => s.value === OTHER_SPECIE_OPTION_VALUE
      )

      speciesIdentified = fs
        ? [
            {
              value: fs?.value,
              title: fs?.name,
            },
          ]
        : []
    } else if (
      speciesOptions &&
      data.specie_identified?.includes(`${UNKNOWN_BAT_SPECIE_OPTION_VALUE}:`)
    ) {
      const fs = speciesOptions.find(
        (s) => s.value === UNKNOWN_BAT_SPECIE_OPTION_VALUE
      )

      speciesIdentified = fs
        ? [
            {
              value: fs?.value,
              title: fs?.name,
            },
          ]
        : []
    } else if (speciesOptions) {
      speciesIdentified = data.specie_identified
        ?.replace(`${OTHER_SPECIE_OPTION_VALUE}:`, '')
        .replace(`${UNKNOWN_BAT_SPECIE_OPTION_VALUE}:`, '')
        .split(',')
        .reduce((acc, si) => {
          const fs = speciesOptions.find((s) => s.value === si.trim())

          if (fs) {
            acc.push({
              value: fs?.value,
              title: fs?.name,
            })
          }

          return acc
        }, [] as AutocompleteOption[])
    } else {
      speciesIdentified = data.specie_identified
    }

    return {
      analysis_status:
        (data.analysis_status === SoundReportStatuses.TODO && !data.analysed) ||
        !data.analysis_status
          ? SoundReportStatuses.DONE
          : data.analysis_status,
      reject_reason: data.reject_reason ?? '',
      second_opinion_reason: data.second_opinion_reason ?? '',
      spectrogram_link: undefined,
      specie_identified: speciesIdentified,
      other_specie: data.specie_identified?.includes(
        `${OTHER_SPECIE_OPTION_VALUE}:`
      )
        ? data.specie_identified.replace(`${OTHER_SPECIE_OPTION_VALUE}:`, '')
        : '',
      unknown_specie: data.specie_identified?.includes(
        `${UNKNOWN_BAT_SPECIE_OPTION_VALUE}:`
      )
        ? data.specie_identified.replace(
            `${UNKNOWN_BAT_SPECIE_OPTION_VALUE}:`,
            ''
          )
        : '',
      analysis_notes: data.analysis_notes ?? '',
      analysis_confidence: data.analysis_confidence ?? '',
      change_observation_log_specie: false,
    }
  }, [data])

  const methods = useForm<SoundRecordFormData>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues,
  })

  useEffect(() => {
    methods.reset(defaultValues)
  }, [defaultValues])

  const handleSubmitForm = async (formData: SoundRecordFormData) => {
    enqueueSnackbar('Saving...', {
      variant: 'default',
    })

    let specieIdentified = ''

    if (
      Array.isArray(formData.specie_identified) &&
      formData.specie_identified.length === 1 &&
      formData.specie_identified[0].value === OTHER_SPECIE_OPTION_VALUE
    ) {
      specieIdentified = `${OTHER_SPECIE_OPTION_VALUE}:${formData.other_specie}`
    } else if (
      Array.isArray(formData.specie_identified) &&
      formData.specie_identified.length === 1 &&
      formData.specie_identified[0].value === UNKNOWN_BAT_SPECIE_OPTION_VALUE
    ) {
      specieIdentified = `${UNKNOWN_BAT_SPECIE_OPTION_VALUE}:${formData.unknown_specie}`
    } else if (
      Array.isArray(formData.specie_identified) &&
      formData.specie_identified.length > 0
    ) {
      specieIdentified = formData.specie_identified.reduce(
        (acc, si) => `${acc?.length > 0 ? `${acc}, ` : ''}${si.value}`,
        ''
      )
    } else if (typeof formData.specie_identified === 'string') {
      specieIdentified = formData.specie_identified
    }

    const params = {
      spectrogram_link: formData.spectrogram_link,
      analysis_status: formData.analysis_status,
      reject_reason: formData.reject_reason,
      second_opinion_reason: formData.second_opinion_reason,
      analysis_notes: formData.analysis_notes,
      analysis_confidence: formData.analysis_confidence,
      specie_identified: specieIdentified,
    }

    try {
      if (
        formData.change_observation_log_specie &&
        formData.specie_identified
      ) {
        const specieNumber = `specie${
          !data.for_specie_number || data.for_specie_number === 1
            ? ''
            : data.for_specie_number
        }`

        await updateObservationLog(
          {
            [specieNumber]: Array.isArray(formData.specie_identified)
              ? formData.specie_identified[0].value
              : formData.specie_identified,
            other_specie: formData.other_specie ?? '',
            unknown_specie: formData.unknown_specie ?? '',
            research_visit_event_id:
              data.observation_log.research_visit_event.id,
            research_observation_log_type_id:
              data.observation_log.research_observation_log_type.id,
          },
          {
            onSuccess: (data) => {
              if (setUpdatedObservationLog) {
                setUpdatedObservationLog(data)
              }
            },
          }
        )
      }

      await editSoundRecord(params, {
        successMessageKey: 'soundRecords:notifications.soundRecordUpdated',
        onSuccess: (data) => {
          if (setUpdatedSoundRecord) {
            setUpdatedSoundRecord(data)
          }

          onSuccessUpdate()
        },
      })
    } catch (e) {
      enqueueSnackbar(`${t('errors:api.execute')}: ${e}`, {
        variant: 'error',
      })
    }
  }

  const handleReset = () =>
    methods.reset({
      ...defaultValues,
    })

  const watchedStatus = methods.watch('analysis_status')
  const watchedSpecie = methods.watch('specie_identified')
  const watchedOtherSpecie = methods.watch('other_specie')
  const watchedUnknownSpecie = methods.watch('unknown_specie')

  const specieNumber = `specie${
    !data.for_specie_number || data.for_specie_number === 1
      ? ''
      : data.for_specie_number
  }` as keyof ObservationLog

  const saveSpecieToObservationLog =
    (Array.isArray(watchedSpecie) &&
      watchedSpecie.length === 1 &&
      (!watchedSpecie?.some(
        (s) => s.value === data.observation_log[specieNumber]
      ) ||
        (watchedSpecie[0].value === OTHER_SPECIE_OPTION_VALUE &&
          !!watchedOtherSpecie &&
          watchedOtherSpecie !== data.observation_log.other_specie) ||
        (watchedSpecie[0].value === UNKNOWN_BAT_SPECIE_OPTION_VALUE &&
          !!watchedUnknownSpecie &&
          watchedUnknownSpecie !== data.observation_log.unknown_specie))) ||
    (typeof watchedSpecie === 'string' &&
      !!watchedSpecie &&
      watchedSpecie !== data.observation_log[specieNumber])

  const options = Object.keys(SoundReportStatuses).map((key) => {
    const status = SoundReportStatuses[
      key as keyof typeof SoundReportStatuses
    ] as string

    return {
      name: t(`forms:soundRecordStatus.${status}`),
      value: status,
    }
  })

  return (
    <form onSubmit={methods.handleSubmit(handleSubmitForm)}>
      <Stack spacing={4}>
        <Box width={360}>
          <ControlledSelect
            name="analysis_status"
            options={options}
            control={methods.control}
            fullWidth={true}
          />
        </Box>
        {watchedStatus === SoundReportStatuses.REJECTED && (
          <Box width={540}>
            <ControlledTextField
              name="reject_reason"
              control={methods.control}
              placeholder={t(
                'soundRecords:sound-record.fieldTitles.rejectReason'
              )}
              multiline={true}
              rows={2}
              inputProps={{
                style: {
                  padding: 0,
                },
              }}
              errorMessagePrefix=""
            />
          </Box>
        )}
        {watchedStatus === SoundReportStatuses.SECOND_OPINION && (
          <Box width={540}>
            <ControlledTextField
              name="second_opinion_reason"
              control={methods.control}
              placeholder={t(
                'soundRecords:sound-record.fieldTitles.secondOpinionReason'
              )}
              multiline={true}
              rows={2}
              inputProps={{
                style: {
                  padding: 0,
                },
              }}
              errorMessagePrefix=""
            />
          </Box>
        )}
        <Box width={160}>
          <ControlledFileUpload
            name="spectrogram_link"
            control={methods.control}
            label={t('soundRecords:sound-record.fieldTitles.spectrogramLink')}
            errorMessagePrefix=""
          />
        </Box>
        <Stack direction="row" spacing={4}>
          {acOptions ? (
            <>
              <Box width={360}>
                <ControlledAutocomplete
                  name="specie_identified"
                  control={methods.control}
                  options={acOptions}
                  multiple={true}
                  placeholder={t(
                    'soundRecords:sound-record.fieldTitles.specieIdentified'
                  )}
                  fullWidth={true}
                />
              </Box>
              {Array.isArray(watchedSpecie)
                ? watchedSpecie?.some(
                    (s) => s.value === OTHER_SPECIE_OPTION_VALUE
                  ) && (
                    <Box width={300}>
                      <ControlledTextField
                        control={methods.control}
                        name="other_specie"
                        placeholder={t(
                          'soundRecords:sound-record.fieldTitles.otherSpecie'
                        )}
                        errorMessagePrefix=""
                      />
                    </Box>
                  )
                : null}
              {Array.isArray(watchedSpecie)
                ? watchedSpecie?.some(
                    (s) => s.value === UNKNOWN_BAT_SPECIE_OPTION_VALUE
                  ) && (
                    <Box width={300}>
                      <ControlledTextField
                        control={methods.control}
                        name="unknown_specie"
                        placeholder={t(
                          'soundRecords:sound-record.fieldTitles.unknownSpecie'
                        )}
                        errorMessagePrefix=""
                      />
                    </Box>
                  )
                : null}
            </>
          ) : (
            <Box width={540}>
              <ControlledTextField
                name="specie_identified"
                control={methods.control}
                placeholder={t(
                  'soundRecords:sound-record.fieldTitles.specieIdentified'
                )}
              />
            </Box>
          )}
          {saveSpecieToObservationLog && (
            <ControlledCheckboxField
              name="change_observation_log_specie"
              control={methods.control}
              label={t(
                'soundRecords:sound-record.fieldTitles.applyFoundSpecieToObservationLog'
              )}
            />
          )}
        </Stack>
        <Box width={540}>
          <ControlledTextField
            name="analysis_confidence"
            control={methods.control}
            label={t(
              'soundRecords:sound-record.fieldTitles.analysisConfidence'
            )}
            errorMessagePrefix=""
          />
        </Box>
        <Box width={540}>
          <ControlledTextField
            name="analysis_notes"
            control={methods.control}
            label={t('soundRecords:sound-record.fieldTitles.analysisNotes')}
            multiline={true}
            rows={3}
            inputProps={{
              style: {
                padding: 0,
              },
            }}
            errorMessagePrefix=""
          />
        </Box>
        {methods.formState.isDirty && (
          <Stack direction="row">
            <Button variant="textThin" color="secondary" onClick={handleReset}>
              {t('forms:buttons.cancel')}
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              color="secondary"
              loading={mutateObservationLogInProcess || mutateInProcess}
              disabled={mutateObservationLogInProcess || mutateInProcess}
              disableElevation={true}
            >
              <span>{t('forms:buttons.save')}</span>
            </LoadingButton>
          </Stack>
        )}
      </Stack>
    </form>
  )
}
